"use client"

import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Plus, Trash2 } from "lucide-react";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import LinkSelector from "../../LinkSelector";
import { Label } from "@/components/ui/label";

export interface TextItem {
    title?: string;
    title_en?: string;
    title_ar?: string;
    description?: string;
    description_en?: string;
    description_ar?: string;
    buttonContent?: string;
    buttonContent_en?: string;
    buttonContent_ar?: string;
    link?: string;
}


interface TextWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const TextWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: TextWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const addItem = () => {
        append({
            title: '',
            title_en: '',
            title_ar: '',
            description: '',
            description_en: '',
            description_ar: '',
            buttonContent: '',
            buttonContent_en: '',
            buttonContent_ar: '',
            link: '',
        });
    };

    const removeItem = (textIndex: number) => {
        if (fields.length <= 1) return;
        remove(textIndex);
    };

    return (
        <>
            {fields.map((field, textIndex) => (
                <div key={field.id} className="border p-4 rounded-md space-y-4">
                    <div className="flex justify-between items-center">
                        <Heading level={4}>
                            {dictionary.ui?.form?.textItem} {textIndex + 1}
                        </Heading>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(textIndex)}
                            disabled={fields.length <= 1 || disabled}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>

                    <MultilingualInput
                        baseName={`${name}.${widgetIndex}.items.${textIndex}.title`}
                        label={dictionary.forms?.title}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />

                    <MultilingualTextareaInput
                        baseName={`${name}.${widgetIndex}.items.${textIndex}.description`}
                        label={dictionary.forms?.description}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />

                    <MultilingualInput
                        baseName={`${name}.${widgetIndex}.items.${textIndex}.buttonContent`}
                        label={dictionary.forms?.buttonContent}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />

                    <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground">
                            {dictionary.forms?.link}
                        </Label>
                        <Controller
                            control={control}
                            name={`${name}.${widgetIndex}.items.${textIndex}.link`}
                            render={({ field: linkField }) => (
                                <LinkSelector
                                    value={linkField.value || ''}
                                    onChange={linkField.onChange}
                                    placeholder={dictionary.forms?.link}
                                />
                            )}
                        />
                    </div>
                </div>
            ))}

            <Button
                type="button"
                variant="outline"
                onClick={addItem}
                disabled={disabled}
                className="mt-2"
            >
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};
